import React, { useState, useEffect } from 'react';
import {
    useAuthContext,
    useCartContext,
    useMessagesContext,
    useModalsContext,
    useStoreConfigContext,
} from '/src/providers';
import {
    Modal,
    QuantityButtons,
    ProductVariantSelect,
    MaxSaleQtyMessage,
} from '/src/components';
import { PRODUCT_TYPES, STOCK_STATUSES } from '/src/consts';
import * as styles from './styles.scss';

const ProductForRecipe = ({ products }) => {
    const { translate } = useStoreConfigContext();
    const { isAuth } = useAuthContext();
    const { showMessage, MESSAGES_TYPES } = useMessagesContext();
    const { showModal, MODALS_TYPES, hideModal } = useModalsContext();
    const { cart: { id: cartId }, isLoadingCart, addProductsToCart } = useCartContext();
    const [quantity, setQuantity] = useState({});
    const [selectedVariants, setSelectedVariants] = useState({});

    const handleAddProducts = () => {
        const cartItems = [];

        products.forEach(({ type_id, sku }) => {
            if (type_id === PRODUCT_TYPES.SIMPLE || type_id === PRODUCT_TYPES.VIRTUAL) {
                cartItems.push({
                    sku,
                    quantity: quantity[sku] || 1,
                });
            }

            if (type_id === PRODUCT_TYPES.CONFIGURABLE) {
                cartItems.push({
                    sku:  selectedVariants[sku],
                    parent_sku: sku,
                    quantity: quantity[sku] || 1,
                })
            }
        });

        addProductsToCart({
            isAuth,
            cartId,
            cartItems,
        }).then(() => {
            showMessage(MESSAGES_TYPES.ADDED_PRODUCT_MESSAGE, { addedProducts: cartItems.map(({ sku }) => sku) });
            hideModal();
        }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }));
    };

    useEffect(() => {
        const selected = {};

        products.forEach(({ sku, default_product_size_for_recipe }) => {
            if (default_product_size_for_recipe) {
                selected[sku] = default_product_size_for_recipe;
            }
        });

        setSelectedVariants({ ...selected });
    }, []);

    return (
        <Modal
            { ...(!isLoadingCart ? { toggle: hideModal } : {}) }
        >
            <div className={styles.scroll}>
                {
                    products.map(({
                        sku,
                        name,
                        stock_status,
                        image: { url, label },
                        stock_data: {
                            max_sale_qty
                        },
                        variants,
                    }) => {
                        const getSelectedVariant = () => variants?.find(({ product: { sku: variantSKU } }) => variantSKU === selectedVariants[sku]);
                        const getMaxSaleQuantity = () => selectedVariants[sku]
                            ? getSelectedVariant()?.product.stock_data.max_sale_qty
                            : max_sale_qty;

                        const maxSaleQuantity = getMaxSaleQuantity();

                        return (
                            <div
                                key={sku}
                                className={styles.item}
                            >
                                <div className={styles.info}>
                                    <img
                                        className={styles.img}
                                        src={`${url}?d=100x100`}
                                        alt={label}
                                    />
                                    <div>
                                        <div className={styles.name}>
                                            {name}
                                        </div>
                                        <div className={styles.status}>
                                            {stock_status === STOCK_STATUSES.IN_STOCK ? translate('In stock') : translate('Out of stock')}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.group}>
                                    <div>
                                        <QuantityButtons
                                            handleRemove={() => setQuantity({ ...quantity, [sku]: (quantity[sku] || 1) - 1 })}
                                            handleAdd={() => setQuantity({ ...quantity, [sku]: (quantity[sku] || 1) + 1 })}
                                            quantity={quantity[sku] || 1}
                                            isLoading={isLoadingCart}
                                            maxSaleQuantity={maxSaleQuantity}
                                        />
                                        <MaxSaleQtyMessage
                                            isMax={(quantity[sku] || 1) === maxSaleQuantity}
                                            message={translate('Maximum Qty Allowed in Shopping Cart %1', maxSaleQuantity)}
                                        />
                                    </div>
                                    {
                                        variants?.length &&
                                        <div className={styles.variants}>
                                            <ProductVariantSelect
                                                setSelectedVariants={setSelectedVariants}
                                                selectedVariants={selectedVariants}
                                                sku={sku}
                                                variants={variants}
                                                translate={translate}
                                                isLoading={isLoadingCart}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        )
                    })
                }
            </div>
            <div className={styles.actions}>
                <button
                    className={styles.close}
                    onClick={hideModal}
                    disabled={isLoadingCart}
                >
                    {translate('Close')}
                </button>
                <button
                    className={styles.add}
                    onClick={handleAddProducts}
                    disabled={isLoadingCart}
                >
                    {translate('Add All Products to Basket')}
                </button>
            </div>
        </Modal>
    );
};

export default ProductForRecipe;
